import { type Notification } from '@/features/notifications/Notification'
import { computed, action } from 'mobx'
import { isSameDay } from 'date-fns'
import type { GetTimeFn } from '@/features/notifications'

/**
 * Notification Item view model.
 */
export class NotificationItemViewModel {
  constructor(
    private readonly _notification: Notification,
    private readonly _getTime: GetTimeFn,
    private readonly _onMarkAsRead: (notification: Notification) => unknown,
  ) {}

  /**
   * The group type for this notification.
   */
  @computed
  get notificationGroupType(): NotificationGroupType {
    return isSameDay(this.dispatchedAt, this._getTime()) ? 'Today' : 'Older'
  }

  /**
   * Notification ID.
   */
  get id() {
    return this._notification.id || ''
  }

  /**
   * Notification message.
   */
  get text() {
    return this._notification.message
  }

  /**
   * Notification message as plain text.
   */
  get plainText() {
    return this._notification.plainText
  }

  /**
   * Workspace name.
   */
  get workspaceName() {
    return this._notification.recipient.workspace.name
  }

  /**
   * Image source.
   */
  get imageSource() {
    return this._notification.imageUrl
  }

  /**
   * Whether the notification has been read.
   */
  get isRead() {
    return this._notification.status === 'Read'
  }

  /**
   * The action URL, if any.
   */
  get actionUrl() {
    return this._notification.actionUrl
  }

  /**
   * The job the notification is about, if any.
   */
  get jobId(): string | null {
    const action = this._notification.action
    return action.type === 'ViewJob' ? action.jobId : null
  }

  /**
   * The time at which the notification was dispatched.
   */
  @computed
  get dispatchedAt(): Date {
    return this._notification.dateDispatch
  }

  /**
   * Marks the notification as read.
   */
  @action.bound
  markAsRead() {
    this._onMarkAsRead(this._notification)
  }
}

/**
 * Group type.
 */
export type NotificationGroupType = 'Today' | 'Older'
